// Persistent manager for word-level quiz validation statuses (Green/Red pastilles)

export type WordQuizStatus = 'CORRECT' | 'WRONG';

const STORAGE_KEY = 'tamazight_quiz_word_statuses_v1';
const EVENT_NAME = 'tamazight_quiz_word_status_updated';

/**
 * Get all stored word quiz statuses from localStorage
 */
export function getAllWordQuizStatuses(): Record<string, WordQuizStatus> {
  if (typeof window === 'undefined' || !window.localStorage) {
    return {};
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.error('Failed to parse quiz word statuses:', err);
    return {};
  }
}

/**
 * Get the quiz validation status for a single Kabyle word
 */
export function getWordQuizStatus(kab: string): WordQuizStatus | null {
  if (!kab) return null;
  const all = getAllWordQuizStatuses();
  return all[kab.trim()] || null;
}

/**
 * Save or update the quiz status of a single word ('CORRECT' = green pastille, 'WRONG' = red pastille)
 */
export function setWordQuizStatus(kab: string, status: WordQuizStatus): void {
  if (!kab || typeof window === 'undefined' || !window.localStorage) return;
  try {
    const all = getAllWordQuizStatuses();
    all[kab.trim()] = status;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { kab: kab.trim(), status } }));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (err) {
    console.error('Failed to save quiz word status:', err);
  }
}

/**
 * Save or update multiple word quiz statuses at once
 */
export function setMultipleWordQuizStatuses(updates: Record<string, WordQuizStatus>): void {
  if (typeof window === 'undefined' || !window.localStorage || Object.keys(updates).length === 0) return;
  try {
    const all = getAllWordQuizStatuses();
    Object.entries(updates).forEach(([kab, status]) => {
      if (kab) {
        all[kab.trim()] = status;
      }
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: updates }));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (err) {
    console.error('Failed to save multiple quiz word statuses:', err);
  }
}

/**
 * Reset word quiz statuses (for specific words or all)
 */
export function resetWordQuizStatuses(kabs?: string[]): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (!kabs || kabs.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      const all = getAllWordQuizStatuses();
      kabs.forEach((k) => delete all[k.trim()]);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
    window.dispatchEvent(new CustomEvent('storage'));
  } catch (err) {
    console.error('Failed to reset quiz word statuses:', err);
  }
}

/**
 * React hook or listener helper for live updates
 */
export function subscribeWordQuizStatuses(
  callback: (statuses: Record<string, WordQuizStatus>) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const handleUpdate = () => {
    callback(getAllWordQuizStatuses());
  };

  window.addEventListener(EVENT_NAME, handleUpdate);
  window.addEventListener('storage', handleUpdate);

  return () => {
    window.removeEventListener(EVENT_NAME, handleUpdate);
    window.removeEventListener('storage', handleUpdate);
  };
}
